import { useContext, useState } from 'react'
import {
  Form,
  FormItem,
  TextInput,
  Uploader,
  Button,
  Fa,
  showErrorMessage,
  Tabs,
  Text,
  TextHeader,
} from '@eltoro-ui/components'
import { useForm, useValidatedState } from '@eltoro-ui/hooks'
import { Creativeservicev1Creative } from 'next-gen-sdk'
import { v4 as uuid } from 'uuid'
import { TextInputWithErrorText, Notification } from 'Components'
import { ThemeContext, useAppContext } from 'Contexts'
import {
  ErrorMessage,
  isValidCharLength,
  styleTailwind,
  validCheck,
} from 'Helpers'
import { CreativesLibraryPage, PreviewFileWithId } from 'Pages'
import { uploadCreative } from 'Requests'
import { NativePreview } from './Components'
import { AdSetDisplay } from '../components'
import './NativeBannerForm.scss'
import classNames from 'classnames'

const NativeBannerLabel = styleTailwind(
  'label',
  'NativeBannerLabel FormItem__label font-semibold flex gap-1 items-center',
)

const Link = styleTailwind(
  'a',
  'NativeBannerLink font-semibold flex items-center leading-none text-base-info',
)

export type DimType = {
  height: number
  width: number
}
type NBprops = {
  handleAddCreative?: (creative: Creativeservicev1Creative) => void
  onUseInNewCampaign?: (creative: Creativeservicev1Creative) => void
  onBackToLibrary?: () => void
}

const NBButton = styleTailwind(
  Button,
  'NativeBanner__button-remove h-auto pl-4 justify-self-end right-0 absolute ml-auto',
)

const UploaderContentInner = styleTailwind(
  'div',
  'UploaderContentInner flex flex-col text-ml text-center w-full',
)

// The 'guts' of the NativeBannerForm. For use in places where the tab structure is not needed.
export const NativeBannerCreate = ({
  handleAddCreative,
  onBackToLibrary,
  onUseInNewCampaign,
}: NBprops) => {
  const [name, setName] = useState<string>('')
  const [icon, setIcon] = useState<PreviewFileWithId>()
  const [logo, setLogo] = useState<PreviewFileWithId>()
  const [iconUploaded, setIconUploaded] = useState(false)
  const [logoUploaded, setLogoUploaded] = useState(false)
  const [logoError, setLogoError] = useState<string>('')
  const [logoWarning, setLogoWarning] = useState<string>('')
  const [iconError, setIconError] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [clickThru, setClickThru, isValidClickThru] = useValidatedState(
    'https://',
    'url',
  )
  const [sponsor, setSponsor] = useState<string>('')
  const [callToAction, setCallToAction] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)
  // const { isPolitical } = useCampaignContext()
  // const { pathname } = useLocation()
  // const isInCampaignCreation =
  //   pathname.includes('/campaigns/create') ||
  //   pathname.includes('/campaigns/edit')
  // const [political, setPolitical] = useState<boolean | null>(
  //   isInCampaignCreation ? isPolitical : null,
  // )
  // const [highlightOptions, setHighlightOptions] = useState(false)
  const { currentOrg, creativeServiceApi, tok } = useAppContext()
  const { name: theme } = useContext(ThemeContext)

  const handleNativeCreation = async (
    iconImg?: PreviewFileWithId,
    logoImg?: PreviewFileWithId,
  ) => {
    if (
      !iconImg ||
      !logoImg ||
      !currentOrg ||
      !currentOrg.id ||
      (!validForm && !iconUploaded && !logoUploaded)
    )
      return

    const logoCreative = await uploadCreative(
      logoImg.fileData.name,
      logoImg.fileData,
      tok,
      currentOrg.id,
      (prog) => {
        console.log(logoImg.id, prog)
      },
      'CREATIVE_FILE_TYPE_MEDIA',
      'CREATIVE_FILE_SUB_TYPE_NATIVE_MAIN',
    )
    const iconCreative = await uploadCreative(
      iconImg.fileData.name,
      iconImg.fileData,
      tok,
      currentOrg.id,
      (prog) => {
        console.log(iconImg.id, prog)
      },
      'CREATIVE_FILE_TYPE_MEDIA',
      'CREATIVE_FILE_SUB_TYPE_NATIVE_ICON',
    )

    if (
      !logoCreative.id ||
      !iconCreative.id ||
      !creativeServiceApi ||
      !currentOrg.id
    )
      return

    creativeServiceApi
      .advertisingPlatformServiceCreateCreative(currentOrg.id, {
        files: [logoCreative, iconCreative],
        name,
        type: 'CREATIVE_TYPE_NATIVE_BANNER',
        nativeMetadata: {
          title,
          bodyText: description,
          callToAction,
          sponsoredBy: sponsor,
          alternativeMobileLandingPageUrl: clickThru,
          fallbackUrl: clickThru,
        },
      })
      .then((res) => {
        if (handleAddCreative) handleAddCreative(res)
      })
      .finally(() => {
        setIcon(undefined)
        setLogo(undefined)
        setTitle('')
        setDescription('')
        setClickThru('')
        setSponsor('')
        setCallToAction('')
        setSuccess(true)
      })
      .catch(() => {
        showErrorMessage(
          'Problem uploading',
          `The banner, ${name}, can't be added into the Creative Library`,
        )
      })
  }

  const getHeightAndWidthFromDataUrl = (url: string) =>
    new Promise<DimType>((resolve) => {
      const img = new Image()
      img.src = url
      img.onload = () => {
        resolve({
          height: img.height,
          width: img.width,
        })
      }
    })

  const getImagePPI = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
      const reader = new FileReader()
      reader.onerror = () => resolve(null)
      reader.onabort = () => resolve(null)
      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer
          const view = new DataView(buffer)
          const len = view.byteLength

          // JPEG: look for APP0 (JFIF) marker with density info
          if (len >= 2 && view.getUint16(0) === 0xffd8) {
            let offset = 2
            while (offset + 4 <= len) {
              const marker = view.getUint16(offset)
              // Stop at SOS (start of scan) — no more metadata after this
              if (marker === 0xffda) break
              if (marker === 0xffe0) {
                // Need offset+11 for xDensity (2 bytes at offset+10)
                if (offset + 12 > len) break
                const units = view.getUint8(offset + 9)
                const xDensity = view.getUint16(offset + 10)
                if (units === 1) {
                  resolve(xDensity) // dots per inch
                  return
                }
                if (units === 2) {
                  resolve(Math.round(xDensity * 2.54)) // dots per cm → dpi
                  return
                }
                resolve(null)
                return
              }
              // Validate segment length before advancing
              if (offset + 4 > len) break
              const segLen = view.getUint16(offset + 2)
              if (segLen < 2) break // invalid segment length
              offset += 2 + segLen
            }
            resolve(null)
            return
          }

          // PNG: look for pHYs chunk
          if (
            len >= 8 &&
            view.getUint32(0) === 0x89504e47 &&
            view.getUint32(4) === 0x0d0a1a0a
          ) {
            let offset = 8
            while (offset + 12 <= len) {
              const chunkLen = view.getUint32(offset)
              // Guard against unreasonable chunk lengths
              if (chunkLen > len - offset - 12) break
              const type = String.fromCharCode(
                view.getUint8(offset + 4),
                view.getUint8(offset + 5),
                view.getUint8(offset + 6),
                view.getUint8(offset + 7),
              )
              if (type === 'pHYs' && chunkLen === 9) {
                // Need offset+16 for unit byte
                if (offset + 17 > len) break
                const pxPerUnitX = view.getUint32(offset + 8)
                const unit = view.getUint8(offset + 16)
                if (unit === 1) {
                  resolve(Math.round(pxPerUnitX / 39.3701)) // meters → inches
                  return
                }
                resolve(null)
                return
              }
              // Stop at IDAT — pHYs must appear before image data
              if (type === 'IDAT') break
              offset += 12 + chunkLen
            }
            resolve(null)
            return
          }

          resolve(null) // GIF or unknown
        } catch {
          resolve(null)
        }
      }
      reader.readAsArrayBuffer(file)
    })
  const { isValidForm, touched } = useForm({
    name,
    title,
    description,
    clickThru,
    callToAction,
    sponsor,
  })
  const required = ['name', 'title', 'description', 'sponsor', 'clickThru']
  const { validForm, missingFields } = isValidForm([
    ...required,
    isValidCharLength(name, 255),
    isValidCharLength(title, 55, 5),
    isValidCharLength(description, 120, 5),
    isValidCharLength(sponsor, 25),
    isValidCharLength(clickThru, 1000, 12),
    isValidCharLength(callToAction, 15),
    logoUploaded,
    iconUploaded,
    isValidClickThru,
  ])

  const NativeBannerSuccess = () => {
    return (
      <div className="NativeBannerCreate__success-msg flex h-full items-center justify-center leading-snug">
        <Notification header="Success!" layOut="row" icon iconType="browser">
          Your Native banner
          {name ? (
            <>
              , <strong>{name}</strong>,&nbsp;
            </>
          ) : (
            ' '
          )}
          has been created.
          <div className="SuccessButtonWrap flex justify-end gap-8 py-6">
            {onBackToLibrary && (
              <Button
                size="l"
                type="submit"
                onClick={() => {
                  setSuccess(false)
                  setName('')
                  onBackToLibrary()
                }}
              >
                Back to library
              </Button>
            )}

            <Button
              size="l"
              onClick={() => {
                setSuccess(false)
                setName('')
              }}
            >
              Create another
            </Button>

            {/* {onUseInNewCampaign && (
              <Button
                size="l"
                kind="primary"
                onClick={() => {
                  if (onUseInNewCampaign && uploadedCreative)
                    onUseInNewCampaign(uploadedCreative)
                }}
              >
                Use in new campaign
              </Button>
            )} */}
          </div>
        </Notification>
      </div>
    )
  }

  return (
    <>
      <div className="NativeBannerCreate laptopsmall:flex-col-reverse -mt-2 flex gap-12 pb-8">
        <div className="NativeBannerForm__form">
          <div className="NativeBannerForm__header">
            <TextHeader
              type={3}
              className="text-tint-gray-600 flex min-w-full text-2xl leading-none"
            >
              Create your Native banner ad below
            </TextHeader>
            <p className="NativeBannerForm__header-text min-w-full pt-2 font-normal leading-normal">
              Native ads have been said to be the future of digital advertising
              as they are not as intrusive or distracting as other forms of
              digital ads, but rather seem native to the page the ad is
              displayed on. Native ads are a great compliment to traditional
              banner ads!
              <span className="Links flex min-w-full gap-3 pt-3 pb-5">
                <Link
                  href="https://eltoro.zendesk.com/hc/en-us/articles/4770083270683-Native-Ads"
                  className="NativeAdsLink__zendesk RightVerticalPipe"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Fa icon="circle-info" size={1} />
                  <span>Read more about Native banner ads</span>
                </Link>
                <Link
                  href="https://eltoro.com/how-to-use-native-ads/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  El Toro's post - Blend in and Stand Out with Native Ads
                </Link>
                <Text className="RequiredLabel text-m ml-auto flex items-center gap-1 pb-2 font-normal leading-none">
                  <span className="text-danger mt-1">*</span>Required
                </Text>
              </span>
            </p>
          </div>
          <Form
            className="NativeBannerForm__form border-t-thin border-tint-gray-200 laptopsmall:flex-col mt-1 flex basis-2/3 gap-8 gap-10 pb-2 pt-8"
            required={required}
            valid={validForm && iconUploaded && logoUploaded}
            touched={touched}
            missingFields={missingFields}
          >
            <div className="NativeBannerForm__form-fields flex flex-col">
              <FormItem
                wrapperClassname="NativeBanner__form--creative-name flex flex-col"
                label={<NativeBannerLabel>Creative name</NativeBannerLabel>}
                htmlFor="name"
                required
                errorMessage={ErrorMessage({
                  fieldName: name,
                  max: 255,
                  label: 'Creative name',
                })}
                valid={validCheck(name) && isValidCharLength(name, 255)}
                counter={name.length > 0}
              >
                <TextInput
                  value={name}
                  placeholder="Displayed in the Creative Library's name column"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setName(e.target.value)
                  }}
                  maxLength={256}
                />
              </FormItem>

              <FormItem
                wrapperClassname="NativeBanner__form--ad-title flex flex-col"
                htmlFor="title"
                label={<NativeBannerLabel>Title</NativeBannerLabel>}
                required
                errorMessage={ErrorMessage({
                  fieldName: title,
                  max: 55,
                  label: 'Title',
                  nativeForm: true,
                  min: 5,
                })}
                valid={validCheck(title) && isValidCharLength(title, 55, 5)}
                counter={title.length > 0}
              >
                <TextInput
                  value={title}
                  placeholder="Headline of Native ad (5–55 characters)"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setTitle(e.target.value)
                  }}
                  maxLength={56}
                  minLength={5}
                />
              </FormItem>
              <FormItem
                wrapperClassname="NativeBanner__form--ad-copy flex flex-col"
                htmlFor="description"
                label={<NativeBannerLabel>Description</NativeBannerLabel>}
                errorMessage={ErrorMessage({
                  fieldName: description,
                  max: 120,
                  label: 'Description',
                  isValid:
                    validCheck(description) &&
                    isValidCharLength(description, 120, 5),
                  nativeForm: true,
                  min: 5,
                })}
                valid={
                  validCheck(description) &&
                  isValidCharLength(description, 120, 5)
                }
                counter={description.length > 0}
                required
              >
                <TextInput
                  placeholder="Short ad copy that engages, persuades, and converts (5–120 characters)"
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={121}
                  minLength={5}
                  value={description}
                />
              </FormItem>
              <FormItem
                wrapperClassname="NativeBanner__form--ad-sponsor flex flex-col"
                htmlFor="sponsor"
                label={<NativeBannerLabel>Sponsor name</NativeBannerLabel>}
                errorMessage={ErrorMessage({
                  fieldName: sponsor,
                  max: 25,
                  label: 'Sponsor',
                })}
                valid={validCheck(sponsor) && isValidCharLength(sponsor, 25)}
                required
                counter={sponsor.length > 0}
              >
                <TextInput
                  placeholder="Name of sponsor or advertiser"
                  value={sponsor}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSponsor(e.target.value)
                  }}
                  maxLength={26}
                />
              </FormItem>
              {/* (It does have the associated control inside TextInputWithErrorText) */}
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <FormItem
                htmlFor="clickThru"
                className="NativeBanner__form--clickthru"
                wrapperClassname="flex flex-col"
                label={
                  <NativeBannerLabel>
                    Click-through URL for your creative
                  </NativeBannerLabel>
                }
                errorMessage={ErrorMessage({
                  fieldName: clickThru,
                  max: 1000,
                  label: 'Click-through URL',
                })}
                counter={clickThru.length > 8}
                required
              >
                <TextInputWithErrorText
                  id="clickThru"
                  value={clickThru}
                  onChange={(e) => setClickThru(e.target.value)}
                  isValid={isValidClickThru}
                  baseValues={['http://', 'https://']}
                  maxLength={1001}
                  minLength={8}
                  popoverContent={(() => {
                    if (!clickThru.includes('http'))
                      return (
                        <div className="PopoverMessage__required">
                          URL requires &quot;https://&quot;
                        </div>
                      )
                    return (
                      <div className="PopoverMessage__message">
                        Please enter a valid url in the format:
                        &quot;https://www.websitename.com&quot;
                      </div>
                    )
                  })()}
                  // errorMsg={false}
                />
              </FormItem>
              <FormItem
                wrapperClassname="NativeBanner__form--cta flex flex-col"
                htmlFor="callToAction"
                label={<NativeBannerLabel>Call to action</NativeBannerLabel>}
                errorMessage={ErrorMessage({
                  fieldName: callToAction,
                  max: 15,
                  label: 'Call to action',
                })}
                valid={
                  validCheck(callToAction) &&
                  isValidCharLength(callToAction, 15)
                }
                counter={callToAction.length > 0}
              >
                <TextInput
                  placeholder="e.g., Learn More (max 15 characters)"
                  value={callToAction}
                  onChange={(e) => {
                    setCallToAction(e.target.value)
                  }}
                  maxLength={16}
                />
              </FormItem>
            </div>
            <div className="NativeBanner__form--image-icon flex flex-col items-stretch gap-8">
              {/* <FormItem
                wrapperClassname={classNames(
                  'NativeBanner__form--political flex flex-col p-2',
                  {
                    'animate-shakex border border-warning rounded bg-warning-50':
                      political === null && highlightOptions,
                  },
                )}
                label={
                  <>
                    <NativeBannerLabel>Type of advertising</NativeBannerLabel>
                    <span className="text-danger mt-1 pl-1">*</span>
                  </>
                }
                htmlFor="political"
              >
                <RadioButtonGroup
                  valueSelected={
                    political !== null ? political.toString() : null
                  }
                  name="Is used for political order lines"
                  onChange={(politicalValue) => {
                    setHighlightOptions(false)
                    setPolitical(politicalValue === 'true')
                  }}
                  layout="vertical"
                >
                  <RadioButton value="true" label="Political" />
                  <RadioButton value="false" label="Non-political" />
                </RadioButtonGroup>
              </FormItem> */}
              <div
                className="NativeBannerForm__uploader-wrap-click-catch"
                // onClick={() => {
                //   if (political === null) setHighlightOptions(true)
                // }}
              >
                <div
                  className={classNames('NativeBannerForm__uploader-wrap', {
                    // 'pointer-events-none opacity-30': political === null,
                    // 'animate-fadein opacity-0': political !== null,
                  })}
                >
                  <FormItem
                    wrapperClassname="NativeBanner__form--ad-image flex flex-col"
                    label={
                      <div className="NativeBanner__uploader relative flex min-w-full items-center">
                        <NativeBannerLabel>Image</NativeBannerLabel>
                        <span className="text-danger mt-1 pl-1">*</span>
                        {logo && (
                          <NBButton
                            onClick={() => {
                              setLogoError('')
                              setLogo(undefined)
                            }}
                            iconOnly={
                              <span
                                aria-label="Click to delete image"
                                data-tooltip="left"
                              >
                                <Fa
                                  className="NativeBannerImage--button-remove"
                                  icon="trash-can"
                                  size={1}
                                />
                              </span>
                            }
                            type="button"
                          />
                        )}
                      </div>
                    }
                    htmlFor="logo"
                    required
                  >
                    {!logo && (
                      <div className="CreativeUploader__wrap">
                        <Uploader
                          className={`UploaderLogo ${
                            logoError
                              ? 'border-warning animate-glowwarning'
                              : ''
                          }`}
                          maxFiles={1}
                          onDrop={async (file) => {
                            setLogoError('')
                            setLogoWarning('')
                            const [f] = file
                            const dims = await getHeightAndWidthFromDataUrl(
                              f.preview,
                            )
                            const fileId: PreviewFileWithId = {
                              fileData: f,
                              id: uuid(),
                            }
                            const maxSize = 3 * 1024 * 1024 // 3MB
                            if (f.size > maxSize) {
                              setLogoError('File size must be under 3MB')
                              return
                            }
                            const validDimensions = [
                              { w: 1200, h: 627 },
                              { w: 600, h: 600 },
                              { w: 800, h: 600 },
                            ]
                            const isValidDim = validDimensions.some(
                              (d) => dims.width === d.w && dims.height === d.h,
                            )
                            if (!isValidDim) {
                              setLogoError(
                                'Dimensions must be 1200x627, 600x600, or 800x600',
                              )
                              return
                            }
                            // Check image resolution (PPI)
                            const ppi = await getImagePPI(f)
                            if (ppi !== null && ppi < 72) {
                              setLogoError(
                                `Image resolution is ${ppi} PPI — minimum 72 PPI required`,
                              )
                              return
                            }
                            setLogo(fileId)
                            setLogoError('')
                            setLogoUploaded(true)
                            if (ppi !== null && ppi < 144) {
                              setLogoWarning(
                                `Image resolution is ${ppi} PPI — 144 PPI recommended for best quality`,
                              )
                            }
                          }}
                          // handleDropDeps={[political]} // triggers uploader re-render when political changes
                        >
                          <UploaderContentInner>
                            <Fa
                              className="mt-auto pb-1"
                              icon="cloud-arrow-up"
                              type="duotone"
                              size={5}
                            />
                            <TextHeader
                              type={5}
                              className="pb-1 font-semibold normal-case"
                            >
                              Click to upload image
                            </TextHeader>
                            <Text className="LogoInstructions text-m">
                              Formats: JPG, PNG, GIF · Max 3MB (750KB
                              recommended)
                              <br />
                              Valid dimensions: 1200×627, 600×600, or 800×600
                              <br />
                              Resolution: Min 72 PPI, recommended 144 PPI
                            </Text>
                          </UploaderContentInner>
                        </Uploader>
                      </div>
                    )}
                    {logoError && (
                      <Text className="animate-slidedown text-m text-warning py-2 font-bold">
                        {logoError}
                      </Text>
                    )}
                    {logoWarning && (
                      <Text className="animate-slidedown text-m text-tint-gray-600 py-2 font-semibold">
                        <Fa icon="circle-info" size={1} /> {logoWarning}
                      </Text>
                    )}
                    {logo && (
                      <figure className="NativeBannerForm__logo animate-slidedown flex flex-col gap-4">
                        <img
                          className="ml-auto h-auto w-full"
                          src={logo && logo.fileData.preview}
                          alt="NativeBanner logo"
                          height={100}
                          width={100}
                        />
                      </figure>
                    )}
                  </FormItem>
                  <FormItem
                    wrapperClassname="NativeBanner__form--ad-icon-logo  flex flex-col"
                    label={
                      <div className="NativeBannerIcon__uploader relative flex min-w-full items-center">
                        <NativeBannerLabel>Icon</NativeBannerLabel>
                        <span className="text-danger mt-1 pl-1">*</span>
                        {icon && (
                          <NBButton
                            onClick={() => {
                              setIconError('')
                              setIcon(undefined)
                            }}
                            iconOnly={
                              <span
                                aria-label="Click to delete icon"
                                data-tooltip="left"
                              >
                                <Fa
                                  className="NativeBannerIcon--button-remove"
                                  icon="trash-can"
                                  size={1}
                                />
                              </span>
                            }
                            type="button"
                          />
                        )}
                      </div>
                    }
                    htmlFor="icon"
                    required
                  >
                    {!icon && (
                      <div className="CreativeUploader__wrap">
                        <Uploader
                          className={`UploaderIcon ${
                            iconError
                              ? 'border-warning animate-glowwarning'
                              : ''
                          }`}
                          isStyled
                          maxFiles={1}
                          onDrop={async (file) => {
                            setIconError('')
                            const dims = await getHeightAndWidthFromDataUrl(
                              file[0].preview,
                            )
                            const fileId: PreviewFileWithId = {
                              fileData: file[0],
                              id: uuid(),
                            }
                            setIcon(fileId)
                            setIconUploaded(true)
                            if (
                              Number((dims.height / dims.width).toFixed(2)) !==
                              1
                            ) {
                              setIconError(
                                '1:1 aspect ratio required (300×300, white background preferred)',
                              )
                            }
                          }}
                          // handleDropDeps={[political]} // triggers uploader re-render when political changes
                        >
                          <UploaderContentInner>
                            <Fa
                              className="mt-auto pb-1"
                              icon="cloud-arrow-up"
                              type="duotone"
                              size={5}
                            />
                            <TextHeader
                              type={5}
                              className="pb-1 font-semibold normal-case"
                            >
                              Click to upload icon
                            </TextHeader>
                            <Text className="IconInstructions text-m">
                              300×300px recommended · 1:1 aspect ratio · White
                              background preferred
                            </Text>
                          </UploaderContentInner>
                        </Uploader>
                      </div>
                    )}

                    {icon && (
                      <figure className="NativeBannerForm__icon animate-slidedown flex flex-col items-center gap-2">
                        <img
                          src={icon && icon.fileData.preview}
                          alt="test"
                          height={50}
                          width={50}
                        />
                      </figure>
                    )}
                    {iconError && (
                      <Text className="animate-slidedown text-m text-warning py-2 font-bold">
                        {iconError}
                      </Text>
                    )}
                  </FormItem>
                </div>
              </div>

              <div className="NativeBannerForm_submit_button animate-slidedown mt-auto flex justify-center gap-4 py-1">
                <Button
                  className={`NativeBannerForm__button px-8 transition-all ${
                    validForm && iconUploaded && logoUploaded
                      ? 'Button--primary ripples'
                      : ''
                  }`}
                  size="l"
                  type="submit"
                  disabled={!validForm}
                  onClick={() => handleNativeCreation(icon, logo)}
                >
                  {validForm && iconUploaded && logoUploaded
                    ? 'Create Native banner'
                    : 'Fill in details to create'}
                </Button>
              </div>
            </div>
          </Form>
        </div>
        <NativePreview
          title={title}
          description={description}
          logo={logo}
          icon={icon}
          sponsor={sponsor}
          callToAction={callToAction}
        />
      </div>
      {/* When form rendered conditionally, causes the error of early return; success component failed to load */}
      {success ? <NativeBannerSuccess /> : null}
    </>
  )
}

export const NativeBannerForm = ({
  refreshLibrary,
}: {
  refreshLibrary?: () => void
}) => {
  const [selectedTab, setSelectedTab] = useState('NativeBanner')
  const [selectedNativeCreatives, setSelectedNativeCreatives] = useState<
    Creativeservicev1Creative[]
  >([])

  const handleSelect = (creative: Creativeservicev1Creative) => {
    setSelectedNativeCreatives((prev) => [...prev, creative])
    setSelectedTab('GetFromLibrary')
  }
  const handleRemove = (creativeId: string) => {
    setSelectedNativeCreatives((prev) =>
      prev.filter((f) => f.id !== creativeId),
    )
  }

  const handleButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    buttonId: string,
  ) => {
    setSelectedTab(buttonId)
  }

  const shouldOpenAdDisplay = !!selectedNativeCreatives.length

  return (
    <div className="laptopsmall:overflow-x-auto NativeBannerForm overflow-y-hidden">
      <div className="NativeBannerForm__header flex items-center gap-3">
        <Tabs
          classNamePanel="flex-1 w-full"
          on="grey-100"
          tabs={[
            {
              id: 'NativeBanner',
              label: (
                <div className="NativeBannerAdForm__header-text-icon flex w-auto items-center justify-center gap-2">
                  <Fa icon="browser" size={4} type="duotone" />
                  <TextHeader
                    className="NativeBannerAdForm__header-text font-normal normal-case"
                    type={5}
                  >
                    Create a Native ad
                  </TextHeader>
                </div>
              ),
              onClick: () => handleButtonClick,
              defaultTab: selectedTab === 'NativeBanner',
              component: (
                <NativeBannerCreate
                  onBackToLibrary={() => {
                    if (refreshLibrary) refreshLibrary()
                    setSelectedTab('GetFromLibrary')
                  }}
                  handleAddCreative={(creative) =>
                    setSelectedNativeCreatives((prev) => [...prev, creative])
                  }
                />
              ),
            },
            {
              id: 'GetFromLibrary',
              label: (
                <>
                  <span
                    aria-label="Select from Creative Library"
                    data-tooltip="left"
                  >
                    <Fa
                      className="text-primary"
                      icon="folders"
                      size={3}
                      type="duotone"
                    />
                  </span>
                  <span className="CreativeTypeSelector__tabs--text">
                    Select from Creative Library
                  </span>
                </>
              ),
              onClick: () => handleButtonClick,
              defaultTab: selectedTab === 'GetFromLibrary',
              component: (
                <CreativesLibraryPage
                  selectedCreatives={selectedNativeCreatives}
                  addToCreativeSet={handleSelect}
                  removeFromCreativeSet={handleRemove}
                  modalPreview
                  windowed
                  filterByType={['CREATIVE_TYPE_NATIVE_BANNER']}
                  emptyState={
                    <div className="NativeBannerForm__library-empty-state text-grey-700 flex flex-col items-center">
                      Oops! There are no Native banner creatives in your
                      library.
                    </div>
                  }
                />
              ),
            },
          ]}
        />
      </div>
      <AdSetDisplay
        shouldOpen={shouldOpenAdDisplay}
        selectedCreatives={selectedNativeCreatives}
        setSelectedCreatives={setSelectedNativeCreatives}
        type="native"
        showBannerChecklist={false}
      />
    </div>
  )
}
